import CheckIcon from '@mui/icons-material/Check';
import DirectionsBoatFilledIcon from '@mui/icons-material/DirectionsBoatFilled';
import { Box, FormControl, FormControlLabel, MenuItem, Pagination, Select, SelectChangeEvent, Stack, Switch, Typography } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useEffect, useState } from 'react';
import { AbsColor } from '../../constants';
import { IRegulation } from '../../contracts/amendment.contract';
import { CalendarFetch } from '../../features/calendar.feature';
import { ShowGuidelinesActions } from '../../features/showGuidelines.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Loading } from '../shared';
import regulationFilterText from '../shared/regulation-filter-text';
import RegulationCards from '../regulation/regulation-cards.component';

const CalendarRegulation = () => {
    const dispatch = useAppDispatch();

    const allVessels = useAppSelector((state) => state.Vessel.List.Vessels);
    const vessels = useAppSelector((state) => state.Calendar.Filters.Vessels);
    const vesselRegulations = useAppSelector((state) => state.Calendar.Regulations.List);
    const isLoading = useAppSelector((state) => state.Calendar.Regulations.IsLoading);
    const showGuidelines = useAppSelector((state) => state.ShowGuidelines.showGuideLines);

    const [regulations, setRegulations] = useState<IRegulation[] | undefined>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);

    const [filteredRegulations, setFilteredRegulations] = useState<IRegulation[] | undefined>([]);
    const [paginatedRegulations, setPaginatedRegulations] = useState<IRegulation[] | undefined>([]);

    useEffect(() => {
        let classNumbers: string[] = [];

        // Get all regulations if no vessels are selected
        if ((!vessels || vessels.length === 0) && allVessels && allVessels.length > 0) {
            classNumbers = allVessels.map((f) => f.ClassNumber);
        } else if (vessels && vessels.length > 0) {
            // Get regulations for selected vessels
            classNumbers = vessels.map((f) => f.ClassNumber);
        }

        const chunkSize = 5;

        for (let i = 0; i < classNumbers.length; i += chunkSize) {
            const chunk = classNumbers.slice(i, i + chunkSize);
            dispatch(CalendarFetch.GetRegulationsByClassNumber(chunk));
        }
    }, [vessels, dispatch]);

    useEffect(() => {
        if (!vesselRegulations) return;

        // Remove duplicate regulations
        let tempRegs = vesselRegulations.filter((reg, index, self) => index === self.findIndex((r) => r.RegulationName === reg.RegulationName && r.RegulationDocName === reg.RegulationDocName));

        // Show guidelines if selected
        if (!showGuidelines) {
            tempRegs = tempRegs.filter((reg) => !reg.RegulationName.toLowerCase().includes('guideline'));
        }

        // Sort the regulations by Regulation Name
        tempRegs.sort((a, b) => a.RegulationName.localeCompare(b.RegulationName));

        setRegulations([...tempRegs]);
    }, [vesselRegulations, showGuidelines]);

    useEffect(() => {
        if (!regulations) return;

        const filtered = searchQuery ? regulationFilterText(searchQuery, regulations) : regulations;
        const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

        setFilteredRegulations([...filtered]);
        setPaginatedRegulations([...paginated]);
    }, [searchQuery, itemsPerPage, currentPage, regulations]);

    const handleClearSearch = () => {
        setSearchQuery('');
        setCurrentPage(1);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const CriteriaFilterStyle = {
        IconStyle: {
            color: AbsColor.White1
        },
        MenuStyle: {
            backgroundColor: AbsColor.Blue1,
            color: AbsColor.White1,
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            width: '100%'
        }
    };

    const customDropdownStyle = {
        formControl: {
            minWidth: 20,
            borderRadius: 4,
            marginRight: '1rem'
        },
        select: {
            padding: '1px 2px'
        }
    };

    const getHeaderTitle = (): string => {
        if (vessels && vessels.length > 0) {
            return `${vessels.length} Vessel${vessels.length > 1 ? 's' : ''}`;
        }

        return `All Vessels`;
    };

    return (
        <Stack direction={'column'} style={{ width: '100%' }} justifyContent={'center'} alignItems={'center'}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1} style={{ width: '100%', height: '50px' }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    <DirectionsBoatFilledIcon style={{ color: AbsColor.Blue1 }} />
                    <div>{getHeaderTitle()}</div>
                </div>
                <FormControlLabel control={<Switch checked={showGuidelines} onChange={(e) => dispatch(ShowGuidelinesActions.toggleShowGuidelines())} />} label="Show Guidelines" />
            </Stack>

            <Stack direction={'column'} gap={1} style={{ width: '100%' }}>
                <ListItemButton style={{ ...CriteriaFilterStyle.MenuStyle }}>
                    <ListItemIcon>
                        <CheckIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                    </ListItemIcon>
                    <ListItemText primary={`${regulations?.length} Regulations` + `${showGuidelines ? ' and Guidelines' : ''}`} />
                </ListItemButton>

                {/* <TextField
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ width: '100%' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleClearSearch}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    size="small"
                /> */}

                {isLoading ? <Loading /> : <>{regulations && <RegulationCards regulations={regulations} />}</>}
            </Stack>
        </Stack>
    );
};

export default CalendarRegulation;
